import "./TechnicalAnalysis.css"; // Styles for this component

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { SearchToken } from "../../types/api";

// Extended token interface for our component
interface TokenOption {
  symbol: string;
  name: string;
  id?: string;
  image?: string | null;
  chain_logo?: string | null;
  base_address?: string | null;
}

type TimeFrame = "Short-term" | "Medium-term" | "Long-term";

interface TimeFrameInfo {
  description: string;
}

const timeFrameInfo: Record<TimeFrame, TimeFrameInfo> = {
  "Short-term": { description: "Days to weeks" },
  "Medium-term": { description: "Weeks to months" },
  "Long-term": { description: "Months to years" },
};

// Define the technical analysis form data interface
export interface TechnicalAnalysisFormData {
  cryptocurrency: string;
  cryptoName?: string;
  cryptoSymbol?: string;
  timeFrame: TimeFrame;
}

// Function to generate template string from form data
const generateTemplateString = (data: TechnicalAnalysisFormData): string => {
  // Format the cryptocurrency display as name/symbol if both are available
  const cryptoDisplay =
    data.cryptoName && data.cryptoSymbol
      ? `${data.cryptoName}/${data.cryptoSymbol}`
      : data.cryptocurrency;

  return `
  I want to analyze the technical indicators for ${cryptoDisplay} with a
  ${data.timeFrame} investment timeframe.
  Please provide technical analysis including key support/resistance levels,
  trend indicators, and potential price targets.`;
};

// Extract header content for the Header component
export const getHeaderContent = () => {
  return {
    iconSrc: "/portfolio_management_icon.png", // Reusing the same icon for now
    iconAlt: "Technical Analysis Icon",
    title: "Technical Analysis",
    subtitle:
      "Select your cryptocurrency and get the latest technical price analysis and trading strategy.",
  };
};

export interface TechnicalAnalysisHandle {
  getFormData: () => TechnicalAnalysisFormData | null;
  generateTemplate: () => string | null;
}

interface TechnicalAnalysisProps {
  onValidityChange?: (isValid: boolean) => void;
}

const TechnicalAnalysis = forwardRef<
  TechnicalAnalysisHandle,
  TechnicalAnalysisProps
>(({ onValidityChange }, ref) => {
  const [selectedCrypto, setSelectedCrypto] = useState<{
    symbol: string;
    name: string;
  } | null>(null);
  const [selectedTimeFrame, setSelectedTimeFrame] =
    useState<TimeFrame>("Short-term");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<TokenOption[]>([]);
  const [cryptoOptions, setCryptoOptions] = useState<TokenOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load popular tokens on component mount
  useEffect(() => {
    const loadPopularTokens = async () => {
      try {
        if (window.openDataApiClient && window.openDataApiClient.search) {
          setIsLoading(true);
          // Fetch popular tokens
          const response = await window.openDataApiClient.search.search({
            types: ["TOKEN"],
            keyword: "", // Empty keyword to get popular tokens
            limit: 5,
          });

          if (response && response.data && response.data.rows) {
            const tokens = response.data.rows.map((token: SearchToken) => ({
              symbol: token.symbol || token.id,
              name: token.name || token.id,
              id: token.id,
              image: token.image,
            }));
            setCryptoOptions(tokens);
          }
        } else {
          // Fallback to static data if API is not available
          setCryptoOptions([
            { symbol: "BTC", name: "Bitcoin" },
            { symbol: "ETH", name: "Ethereum" },
            { symbol: "SOL", name: "Solana" },
            { symbol: "XRP", name: "XRP" },
            { symbol: "DOGE", name: "Dogecoin" },
          ]);
        }
      } catch (err) {
        console.error("Error loading popular tokens:", err);
        // Fallback to static data
        setCryptoOptions([
          { symbol: "BTC", name: "Bitcoin" },
          { symbol: "ETH", name: "Ethereum" },
          { symbol: "SOL", name: "Solana" },
          { symbol: "XRP", name: "XRP" },
          { symbol: "DOGE", name: "Dogecoin" },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    loadPopularTokens();
  }, []);

  // Determine if form is valid
  const isFormValid = selectedCrypto !== null;

  // Report validity change to parent
  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isFormValid);
    }
  }, [isFormValid, onValidityChange]);

  // Function to search tokens using the iframe API
  const searchTokens = async (keyword: string) => {
    if (!keyword) {
      setFilteredOptions([]);
      setIsDropdownOpen(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log(window.openDataApiClient);
      // Check if the API client is available in the window object
      if (window.openDataApiClient && window.openDataApiClient.search) {
        const response = await window.openDataApiClient.search.search({
          types: ["TOKEN"],
          keyword: keyword,
          operator: "PREFIX",
          limit: 10,
        });

        // Process the response data
        if (response && response.data && response.data.rows) {
          console.log(response.data.rows);
          const tokens = response.data.rows.map((token: SearchToken) => ({
            symbol: token.symbol || token.id,
            name: token.name || token.id,
            id: token.id,
            image: token.image,
            chain_logo: token.chain_logo,
            base_address: token.base_address,
          }));

          // Only update filtered options, not the candidate tokens
          setFilteredOptions(tokens);
          setIsDropdownOpen(tokens.length > 0);
        } else {
          setFilteredOptions([]);
          setIsDropdownOpen(false);
        }
      } else {
        // Fallback to static data if API is not available
        const staticOptions = [
          { symbol: "BTC", name: "Bitcoin" },
          { symbol: "ETH", name: "Ethereum" },
          { symbol: "SOL", name: "Solana" },
          { symbol: "XRP", name: "XRP" },
          { symbol: "DOGE", name: "Dogecoin" },
        ];

        const filtered = staticOptions.filter(
          (option) =>
            option.name.toLowerCase().includes(keyword.toLowerCase()) ||
            option.symbol.toLowerCase().includes(keyword.toLowerCase())
        );

        setCryptoOptions(staticOptions);
        setFilteredOptions(filtered);
        setIsDropdownOpen(filtered.length > 0);
      }
    } catch (err) {
      console.error("Error searching tokens:", err);
      setError("Failed to fetch token data");

      // Fallback to static data
      const staticOptions = [
        { symbol: "BTC", name: "Bitcoin" },
        { symbol: "ETH", name: "Ethereum" },
        { symbol: "SOL", name: "Solana" },
        { symbol: "XRP", name: "XRP" },
        { symbol: "DOGE", name: "Dogecoin" },
      ];

      setCryptoOptions(staticOptions);
      const filtered = staticOptions.filter(
        (option) =>
          option.name.toLowerCase().includes(keyword.toLowerCase()) ||
          option.symbol.toLowerCase().includes(keyword.toLowerCase())
      );
      setFilteredOptions(filtered);
      setIsDropdownOpen(filtered.length > 0 && keyword.length > 0);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search to avoid too many API calls
  const debounce = <T extends (arg: string) => void>(
    func: T,
    delay: number
  ) => {
    let timeoutId: NodeJS.Timeout;
    return (arg: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(arg), delay);
    };
  };

  // Create a debounced search function
  const debouncedSearch = useRef(debounce(searchTokens, 300)).current;

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);

    // If the input is cleared, reset the selected cryptocurrency
    // This will make the form invalid and disable the Start Analysis button
    if (!value.trim()) {
      setSelectedCrypto(null);
      setFilteredOptions([]);
      setIsDropdownOpen(false);
    } else {
      debouncedSearch(value);
    }
  };

  const handleSelectCrypto = (option: TokenOption) => {
    setSelectedCrypto({ symbol: option.symbol, name: option.name });
    setSearchTerm(`${option.name} (${option.symbol})`);
    setIsDropdownOpen(false);
  };

  const handleTimeFrameSelect = (timeFrame: TimeFrame) => {
    setSelectedTimeFrame(timeFrame);
  };

  const handleCandidateTokenSelect = (symbol: string) => {
    // First check if the symbol exists in cryptoOptions
    let option = cryptoOptions.find((opt) => opt.symbol === symbol);

    // If not found in cryptoOptions, check in static options
    if (!option) {
      const staticOptions = [
        { symbol: "BTC", name: "Bitcoin" },
        { symbol: "ETH", name: "Ethereum" },
        { symbol: "SOL", name: "Solana" },
        { symbol: "XRP", name: "XRP" },
        { symbol: "DOGE", name: "Dogecoin" },
      ];
      option = staticOptions.find((opt) => opt.symbol === symbol);
    }

    if (option) {
      // Set the search term to the token symbol
      const symbolText = option.symbol;
      setSelectedCrypto({ symbol: option.symbol, name: option.name });
      setSearchTerm(symbolText);

      // Trigger search with the symbol
      searchTokens(symbolText);
      setIsDropdownOpen(false);

      // Focus the input field after selection for better UX
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  // Expose form data and template generation to the parent component via ref
  useImperativeHandle(ref, () => ({
    getFormData: () => {
      if (isFormValid && selectedCrypto) {
        return {
          cryptocurrency: selectedCrypto.symbol,
          cryptoName: selectedCrypto.name,
          cryptoSymbol: selectedCrypto.symbol,
          timeFrame: selectedTimeFrame,
        };
      }
      return null;
    },
    generateTemplate: () => {
      if (isFormValid && selectedCrypto) {
        return generateTemplateString({
          cryptocurrency: selectedCrypto.symbol,
          cryptoName: selectedCrypto.name,
          cryptoSymbol: selectedCrypto.symbol,
          timeFrame: selectedTimeFrame,
        });
      }
      return null;
    },
  }));

  return (
    <main className="body-container">
      {/* Cryptocurrency Search Section */}
      <div className="form-section">
        <label className="form-label">
          Which cryptocurrency are you interested in?
        </label>
        <div className="search-container" ref={dropdownRef}>
          <div className="input-container">
            <input
              type="text"
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => handleSearchTermChange(e.target.value)}
              onFocus={() =>
                setIsDropdownOpen(
                  searchTerm.length > 0 && filteredOptions.length > 0
                )
              }
              className="form-input"
              placeholder="Enter cryptocurrency name or symbol (e.g., Bitcoin or BTC)"
            />
          </div>

          {/* Search Dropdown */}
          {isDropdownOpen && (
            <div className="search-dropdown">
              {isLoading ? (
                <div className="dropdown-item loading">Searching...</div>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.id || option.symbol}
                    className="dropdown-item"
                    onClick={() => handleSelectCrypto(option)}
                  >
                    <div className="token-icon-container">
                      {option.image ? (
                        <img
                          src={option.image}
                          alt={option.symbol}
                          className="crypto-icon"
                          onError={(e) => {
                            // Handle image loading error
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      ) : (
                        <div className="crypto-icon-placeholder"></div>
                      )}
                      {option.chain_logo && (
                        <img
                          src={option.chain_logo}
                          alt="Chain"
                          className="chain-icon"
                          onError={(e) => {
                            // Handle image loading error
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      )}
                    </div>
                    <div className="token-details">
                      <div className="crypto-info">
                        <span className="crypto-name">{option.name}</span>
                        <span className="crypto-symbol">/{option.symbol}</span>
                      </div>
                      {option.base_address && (
                        <div>
                          <span className="crypto-address">
                            {option.base_address.substring(0, 6)}...
                            {option.base_address.substring(
                              option.base_address.length - 4
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="dropdown-item no-results">No tokens found</div>
              )}
            </div>
          )}
        </div>

        {/* Candidate Token List */}
        <div className="candidate-tokens">
          {isLoading ? (
            <div className="loading-indicator">Loading...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            // Show popular tokens or the filtered results if available
            (cryptoOptions.length > 0
              ? cryptoOptions
              : [
                  { symbol: "BTC", name: "Bitcoin" },
                  { symbol: "ETH", name: "Ethereum" },
                  { symbol: "SOL", name: "Solana" },
                  { symbol: "XRP", name: "XRP" },
                  { symbol: "DOGE", name: "Dogecoin" },
                ]
            )
              .slice(0, 5)
              .map((option) => (
                <button
                  key={option.symbol || option.id}
                  className={`token-button ${
                    selectedCrypto?.symbol === option.symbol ? "selected" : ""
                  }`}
                  onClick={() => handleCandidateTokenSelect(option.symbol)}
                >
                  {option.symbol}
                </button>
              ))
          )}
        </div>
      </div>

      {/* Timeframe Selection Section */}
      <div className="form-section">
        <label className="form-label">
          What&apos;s your investment timeframe?
        </label>
        <div className="risk-level-options">
          {(["Short-term", "Medium-term", "Long-term"] as TimeFrame[]).map(
            (timeFrame) => (
              <div
                key={timeFrame}
                className={`risk-level-option ${
                  selectedTimeFrame === timeFrame ? "selected" : ""
                }`}
                onClick={() => handleTimeFrameSelect(timeFrame)}
              >
                <div className="timeframe-icon">
                  {timeFrame === "Short-term" && (
                    <svg
                      className="timeframe-svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 13H5V11H3V13ZM3 17H5V15H3V17ZM3 9H5V7H3V9ZM7 13H21V11H7V13ZM7 17H21V15H7V17ZM7 7V9H21V7H7Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                  {timeFrame === "Medium-term" && (
                    <svg
                      className="timeframe-svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                  {timeFrame === "Long-term" && (
                    <svg
                      className="timeframe-svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20ZM12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </div>
                <p className="risk-level-title">{timeFrame}</p>
                <p className="risk-level-description">
                  {timeFrameInfo[timeFrame].description}
                </p>
              </div>
            )
          )}
        </div>
      </div>
    </main>
  );
});

TechnicalAnalysis.displayName = "TechnicalAnalysis";

// Add TypeScript interface for the window object to include openDataApiClient
// API interfaces are now imported from src/types/api.ts

export default TechnicalAnalysis;
